import { useEffect } from "react";
import { useLocation } from "react-router";

import {
  OFFLINE_PATHS,
  mayAutoRecover,
  probeDelayMs,
  recoveryTarget,
} from "~/lib/pwa/offline-fallback";

/**
 * The build-identity file (`buildIdentityPlugin`, `vite.config.ts`): 40 static bytes
 * outside the Functions (`public/_routes.json` excludes it), so a probe costs no Function
 * request, and never precached, so only the network can answer it.
 */
const PROBE_URL = "/__build.json";
const ATTEMPTS_KEY = "tfc-offline-recovery";
/** Probes waiting at once; a new one past this aborts the oldest. */
const MAX_PROBES_IN_FLIGHT = 3;
/** A probe still unanswered after this long is abandoned (no request lives forever). */
const PROBE_MAX_AGE_MS = 30_000;

/**
 * T841 — when the offline page stands in for a URL she asked for (`?from=`, set by the
 * client boot), send her there once the network answers, with no tap (R-T841-2).
 *
 * `navigator.onLine` alone is not enough: it is `true` during a block window (T535,
 * `unreachable`), and a navigation that fails there lands right back on this page. So
 * every automatic recovery waits for a no-store probe to answer, and `mayAutoRecover`
 * caps them per target (R-T841-3). Offline nothing is requested at all.
 *
 * Returns the target for the retry button, or `null` when there is none.
 */
export function useOfflineRecovery(): string | null {
  const { search } = useLocation();
  const target = recoveryTarget(search, OFFLINE_PATHS);

  useEffect(() => {
    if (!target) return;
    let cancelled = false;
    let done = false;
    let launches = 0;
    let next: number | undefined;
    /**
     * Probes in flight, oldest first. Launch times follow ONE schedule — at once, then 2 s,
     * 6 s, 14 s, 30 s … after it (`probeDelayMs`) — whatever each probe does: a slow probe is
     * not cut by its own clock (only the three-probe cap or the 30 s expiry ends it), and a
     * hung or failing one never delays the next. The first answer wins. Every
     * schedule built on probe outcomes lost a case to R-T841-2's 10 s (her QA of `f6c461e`,
     * infra audit rounds 4–7); this one lands by the third launch (6 s) plus its latency.
     */
    const inFlight = new Set<AbortController>();

    const stopAll = () => {
      for (const probe of inFlight) probe.abort();
      inFlight.clear();
      window.clearTimeout(next);
    };

    const launch = () => {
      if (cancelled || done || !window.navigator.onLine) return;
      if (inFlight.size >= MAX_PROBES_IN_FLIGHT) {
        const oldest = inFlight.values().next().value;
        oldest?.abort();
        if (oldest) inFlight.delete(oldest);
      }
      const probe = new AbortController();
      inFlight.add(probe);
      const expire = window.setTimeout(() => probe.abort(), PROBE_MAX_AGE_MS);
      window.clearTimeout(next);
      next = window.setTimeout(launch, probeDelayMs(launches));
      launches += 1;
      void fetch(PROBE_URL, { cache: "no-store", signal: probe.signal })
        .then(
          (res) => res.ok,
          () => false,
        )
        .then((answered) => {
          window.clearTimeout(expire);
          const current = inFlight.delete(probe);
          if (!answered || cancelled || done || !current) return;
          done = true;
          stopAll();
          if (recordAttempt(target)) window.location.replace(target);
        });
    };

    const onOnline = () => {
      launches = 0;
      launch();
    };

    window.addEventListener("online", onOnline);
    launch();
    return () => {
      cancelled = true;
      stopAll();
      window.removeEventListener("online", onOnline);
    };
  }, [target]);

  return target;
}

/**
 * Called by the client boot on every NORMAL document: a page that is not the offline one
 * booted, so whatever recovery was pending in this tab landed. Clearing here is what lets
 * `mayAutoRecover` read a record as "a recovery that has not landed" (T841 audit round 11).
 */
export function clearOfflineRecoveryRecords(): void {
  try {
    const storage = window.sessionStorage;
    for (let i = storage.length - 1; i >= 0; i -= 1) {
      const key = storage.key(i);
      if (key?.startsWith(`${ATTEMPTS_KEY}:`)) storage.removeItem(key);
    }
  } catch {
    // Storage blocked: there is nothing recorded to clear.
  }
}

/**
 * Records this recovery and says whether it may run. Without storage there is no record, so
 * nothing could stop a failed navigation from repeating on every boot of this page: no
 * automatic recovery then — the retry button still takes her there (T841 audit round 13).
 */
function recordAttempt(target: string): boolean {
  const key = `${ATTEMPTS_KEY}:${target}`;
  const now = Date.now();
  try {
    const parsed: unknown = JSON.parse(window.sessionStorage.getItem(key) ?? "[]");
    const attempts = Array.isArray(parsed) ? parsed.filter((at): at is number => typeof at === "number") : [];
    if (!mayAutoRecover(attempts, now)) return false;
    window.sessionStorage.setItem(key, JSON.stringify([...attempts, now]));
    return true;
  } catch {
    return false;
  }
}
